// NPM Imports
import React, { Component } from "react";
import { connect } from "react-redux";
import ReactHtmlParser from "react-html-parser";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Resizable } from "react-resizable";
import { cloneDeep } from "lodash";
import { Tooltip } from "@revvsales/components";

// Components Imports
import { ConfirmationBox, UpgradePopUp } from "../../commonComponents";
import JustificationButtons from "./JustificationButtons";
import DropzoneFiles from "../../commonComponents/DropzoneFiles";
import ConvertToImageFieldBtn from "./ConvertToImageFieldBtn";

// Actions Imports
import {
  updateDocumentClickedComponentID,
  forceUpdateEditorSectionIdAction,
  updateDynamicFieldsInDoc,
} from "../../actions/DocumentActions/index";
import {
  convertToImageField,
  editImageFormField,
} from "../../actions/DocumentTemplateActions/templateActions";
import { getComponentFromInfo } from "../../utils/documentUtils";

// services import
import { uploadImageService } from "../../actions/DocumentActions/services";

// Internal Imports
import {
  MAX_IMAGE_HEIGHT,
  MAX_IMAGE_WIDTH,
  INITIAL_IMAGE_HEIGHT,
  INITIAL_IMAGE_WIDTH,
  MIN_IMAGE_HEIGHT,
  MIN_IMAGE_WIDTH,
  IMAGE_RENDERING_DETAILS,
} from "../../constants/images";
import { AmplitudeUtils, GlobalUtils } from "../../utils";
import { EVENTS, DOCUMENTS, FEATURES } from "../../constants";
import CustomImageOptions from "./CustomImageOptions/CustomImageOptions";

// Destructuring
const { EVENT_NAMES } = EVENTS;
const { amplitudeLogEvent } = AmplitudeUtils;
const { COMPONENT_FEATURE } = DOCUMENTS;
const { FEATURES_NAME } = FEATURES;
const { readDataFromLocalStorage } = GlobalUtils;

class CustomImage extends Component {
  optionsDropDownRef = React.createRef();

  constructor(props) {
    super(props);
    this.state = {
      errMsg: "",
      imgWidth: null,
      imgHeight: null,
      imgJustify: "flex-start",
      altText: "",
      checkImgUploadLoader: false,
      showRemoveImageFormFieldConfirmation: false,
      showConvertImageFormFieldUpgradePopup: false,
    };
    this.imgContainerRef = React.createRef();
  }

  async componentDidMount() {
    await this._updateImageUI();
  }

  componentDidUpdate() {
    if (this.props.forceUpdateEditorSectionId === this.props.section.id) {
      this._updateImageUI();
      this.props.forceUpdateEditorSectionIdAction(null);
    }
  }

  _getImageRenderingDetails = () => {
    const { mode } = this.props;
    let modeType = "EDITOR_MODE";
    let imageType = "NORMAL_IMAGE";
    // Check if the section has an image field
    if (
      this.props.section &&
      this.props.section.clauseOptions &&
      this.props.section.clauseOptions.id
    ) {
      imageType = "IMAGE_FIELD";
    }
    // Check for the document rendering type (ex: form mode, form perma link, editor perma link)
    if (mode === DOCUMENTS.DOCUMENT_MODE.DOCUMENT) {
      // Form mode case
      if (this.props.showForm) {
        modeType = "FORM_MODE";
      } else if (
        // Perma(Magic) link mode case
        window.location &&
        window.location.pathname &&
        (window.location.pathname.startsWith("/perma/DOC/") ||
          window.location.pathname.startsWith("/m/"))
      ) {
        if (this.props.templateType === "form") {
          modeType = "FORM_PERMA_LINK_MODE";
        } else if (this.props.templateType === "editor") {
          modeType = "EDITOR_PERMA_LINK_MODE";
        }
      }
    }
    let imageRenderingDetails = {};
    if (
      IMAGE_RENDERING_DETAILS[mode] &&
      IMAGE_RENDERING_DETAILS[mode][modeType] &&
      IMAGE_RENDERING_DETAILS[mode][modeType][imageType]
    ) {
      imageRenderingDetails =
        IMAGE_RENDERING_DETAILS[mode][modeType][imageType];
    }
    return imageRenderingDetails;
  };

  getImageDimension = (data, imgJustify, altText) =>
    new Promise(res => {
      if (data instanceof Array) {
        const file = new FileReader();

        const img = new Image();
        img.onload = () => {
          const { width, height } = this._reduceDimensions(
            img.width,
            img.height,
          );
          if (imgJustify && altText) {
            this.setState(
              {
                imgWidth: width,
                imgHeight: height,
                imgJustify,
                altText,
              },
              () => res(true),
            );
          } else {
            this.setState(
              {
                imgWidth: width,
                imgHeight: height,
              },
              () => res(true),
            );
          }
        };
        file.onload = () => {
          img.src = file.result;
        };
        file.readAsDataURL(data[0]);
      } else {
        const img = new Image();
        img.onload = () => {
          const { width, height } = this._reduceDimensions(
            img.width,
            img.height,
          );
          if (imgJustify && altText) {
            this.setState(
              {
                imgWidth: width,
                imgHeight: height,
                imgJustify,
                altText,
              },
              () => res(true),
            );
          } else {
            this.setState(
              {
                imgWidth: width,
                imgHeight: height,
              },
              () => res(true),
            );
          }
        };
        img.src = data;
      }
    });

  _getImageDimension = data =>
    new Promise(res => {
      if (data instanceof Array) {
        const file = new FileReader();
        const img = new Image();
        img.onload = () => {
          const newDimensions = this._reduceDimensions(img.width, img.height);
          res(newDimensions);
        };
        file.onload = () => {
          img.src = file.result;
        };
        file.readAsDataURL(data[0]);
      } else {
        const img = new Image();
        img.onload = () => {
          const newDimensions = this._reduceDimensions(img.width, img.height);
          res(newDimensions);
        };
        img.src = data;
      }
    });

  _reduceDimensions = (width, height) => {
    // reduce the dimensions if it exceeds max limit
    let newDimensions = { width, height };
    if (width > MAX_IMAGE_WIDTH) {
      newDimensions = {
        width: MAX_IMAGE_WIDTH,
        height: MAX_IMAGE_WIDTH * (height / width),
      };
    }
    if (height > MAX_IMAGE_HEIGHT) {
      newDimensions = {
        height: MAX_IMAGE_HEIGHT,
        width: MAX_IMAGE_HEIGHT * (width / height),
      };
    }
    return newDimensions;
  };

  getBase64 = file =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        resolve(reader.result);
      };
      reader.onerror = error => reject(error);
    });

  _getBase64FromHtml = htmlString => {
    try {
      const parser = new DOMParser();
      const htmlDoc = parser.parseFromString(htmlString, "text/xml");
      const srcValue = htmlDoc.getElementsByTagName("img")[0].attributes.src
        .value;
      return srcValue;
    } catch (error) {
      return htmlString;
    }
  };

  _isBase64 = imageString => {
    if (
      typeof imageString === "string" &&
      (imageString.startsWith("data") ||
        (imageString.startsWith("<") && imageString.includes("<img ")))
    ) {
      return true;
    }
    return false;
  };

  _isHtmlString = dataString =>
    !!(dataString.startsWith("<p>") || dataString.startsWith("<img"));

  _updateUploadedImage = (uploadImgLink, imgWidth, imgHeight) =>
    JSON.stringify({
      image: uploadImgLink,
      imgWidth,
      imgHeight,
      imgJustify: this.state.imgJustify,
      altText: this.state.altText,
    });

  _onImageUpload = fileItems => {
    // Case 1 :: if it is a case of image file upload
    if (typeof fileItems === "object" && fileItems instanceof Array) {
      if (fileItems.length > 0) {
        const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
        // Check if the uploaded file is an image of type jpeg, jpg or png
        if (allowedTypes.indexOf(fileItems[0].type) === -1) {
          this.setState({
            errMsg:
              "This file type is not supported. Try uploading .jpeg, .jpg or .png",
          });
        } else {
          this.setState({
            checkImgUploadLoader: true,
            imgWidth: null,
            imgHeight: null,
          });
          this._getImageDimension(fileItems).then(imageDimension => {
            uploadImageService(fileItems[0])
              .then(res => {
                const imgData = this._updateUploadedImage(
                  res.Image.Path,
                  imageDimension.width,
                  imageDimension.height,
                );
                const newSection = cloneDeep(this.props.section);
                // If it is a case of Image field then, remove the image field
                const { isImageField } = this._getImageRenderingDetails();
                if (isImageField) {
                  newSection.clauseOptions = null;
                }
                newSection.data = imgData;
                this.props.handleBlur(newSection, undefined, undefined, {
                  ...this.props.section,
                });
              })
              .catch(error => {
                console.error(
                  `Customimage => _onImageUpload =>  Error :`,
                  error,
                );
              });
          });
        }
      }
    }
    // Case 2 :: if it is a case of choosing image from "Unsplash"
    else if (typeof fileItems === "string") {
      this.setState({
        checkImgUploadLoader: true,
        imgWidth: null,
        imgHeight: null,
      });
      this._getImageDimension(fileItems).then(imageDimension => {
        const imgData = this._updateUploadedImage(
          fileItems,
          imageDimension.width,
          imageDimension.height,
        );
        const newSection = cloneDeep(this.props.section);
        newSection.data = imgData;
        // If it is a case of Image field then, remove the image field
        const { isImageField } = this._getImageRenderingDetails();
        if (isImageField) {
          newSection.clauseOptions = null;
        }
        this.props.handleBlur(newSection, undefined, undefined, {
          ...this.props.section,
        });
        amplitudeLogEvent(EVENT_NAMES.UNSPLASH_IMAGE_UPLOAD, {});
      });
    }
  };

  getSection = () => {
    // check if data is base 64 and handle section data accordingly
    const { section, data } = this.props;
    let { imgHeight, imgWidth } = this.state;
    // Check if there are no "imgWidth" or "imgHeight" in the state
    // then, get the img width and height from the image data
    if (!imgHeight || !imgWidth) {
      const {
        imgHeight: actualImgHeight,
        imgWidth: actualImgWidth,
      } = this._getImageDetails(false);
      imgHeight = actualImgHeight;
      imgWidth = actualImgWidth;
    }
    const oldSection = cloneDeep(section);
    if (this._isBase64(data)) {
      oldSection.data = JSON.stringify({
        image: data,
        imgWidth,
        imgHeight,
        imgJustify: this.state.imgJustify,
        altText: this.state.altText,
      });
    } else {
      const newImageData = JSON.parse(data);
      newImageData.imgHeight = imgHeight;
      newImageData.imgWidth = imgWidth;
      newImageData.imgJustify = this.state.imgJustify;
      newImageData.altText = this.state.altText;
      oldSection.data = JSON.stringify(newImageData);
    }
    return oldSection;
  };

  _handleOnResizeStop = () => {
    const {
      isImageField,
      allowResize,
      allowImageFieldUpdate,
    } = this._getImageRenderingDetails();
    if (
      (!this.props.isLocked ||
        (isImageField && allowResize && !this.props.isImagefieldLocked)) && // image field case
      this.props.isComponentActive
    ) {
      if (isImageField && allowImageFieldUpdate) {
        if (
          this.props.imageFormFieldDetails &&
          this.props.imageFormFieldDetails.value
        ) {
          let newImageData = {};
          if (this._isBase64(this.props.imageFormFieldDetails.value)) {
            newImageData.image = this.props.imageFormFieldDetails.value;
          } else {
            newImageData = JSON.parse(this.props.imageFormFieldDetails.value);
          }
          newImageData.imgHeight = this.state.imgHeight;
          newImageData.imgWidth = this.state.imgWidth;
          const updatedImage = JSON.stringify(newImageData);

          if (updatedImage) {
            const { sharingHash } =
              this.props.match && this.props.match.params
                ? this.props.match.params
                : {};

            if (sharingHash) {
              this.props.updateDynamicFieldsInDoc(
                this.props.section.clauseOptions.id,
                updatedImage,
                true,
                sharingHash,
              );
            } else {
              this.props.updateDynamicFieldsInDoc(
                this.props.section.clauseOptions.id,
                updatedImage,
              );
            }
          }
        }
      } else {
        const newSection = this.getSection();
        if (
          isImageField &&
          this.props.mode === DOCUMENTS.DOCUMENT_MODE.DOCUMENT &&
          !this.props.showForm
        ) {
          newSection.clauseOptions = null;
        }
        this.props.handleBlur(newSection, undefined, undefined, {
          ...this.props.section,
        });
      }
    }
  };

  _handleJustification = position => {
    // set jsutifucation in local state and call api to update globally/DB.
    if (
      position === "flex-start" ||
      position === "center" ||
      position === "flex-end"
    ) {
      const { section, handleBlur } = this.props;
      this.setState({ imgJustify: position }, () => {
        const newSection = this.getSection();
        this.props.disablePlusBtnHandler(false);
        handleBlur(newSection, undefined, undefined, { ...section });
        this.props.updateDocumentClickedComponentID(
          section.id,
          false,
          section.type,
        );
      });
    }
  };

  _onResize = (event, { size }) => {
    const { isImageField, allowResize } = this._getImageRenderingDetails();
    if (
      (!this.props.isLocked || (isImageField && allowResize)) &&
      !this.props.lock
    ) {
      if (
        size.width <= MAX_IMAGE_WIDTH / this.props.totalRowItems &&
        size.height <= MAX_IMAGE_HEIGHT &&
        size.width >= MIN_IMAGE_WIDTH &&
        size.height >= MIN_IMAGE_HEIGHT
      ) {
        this.setState({
          imgWidth: size.width,
          imgHeight: size.height,
        });
      }
    }
  };

  _getImageComponent = (
    imageData,
    altText,
    imgActualInitialWidth,
    imgActualInitialHeight,
  ) => {
    let imageHTML;
    // Check if image data is in string format
    if (typeof imageData === "string") {
      if (imageData.startsWith("data")) {
        imageHTML = (
          <img className="section-image" src={imageData} alt={altText} />
        );
      } else if (imageData.startsWith("http")) {
        imageHTML = (
          <img className="section-image" src={imageData} alt={altText} />
        );
      } else if (imageData.includes("<img src=")) {
        const elem = document.createElement("div");
        elem.innerHTML = imageData;
        const images = elem.getElementsByTagName("img");
        for (let i = 0; i < images.length; i += 1) {
          images[i].classList.add("section-image");
        }
        if (images.length > 0) {
          imageHTML = ReactHtmlParser(elem.innerHTML);
        }
      }
    }

    if (!imageHTML) {
      imageHTML = (
        <img
          className="section-image"
          src={this.props.imageBaseUrl + imageData}
          alt={altText}
        />
      );
    }

    // Check if there are no "imgWidth" or "imgHeight" then, get the
    // "imgActualInitialWidth" or "imgActualInitialHeight" accordingly
    // (ex: this scenario will happen in image replace case and
    //  in image field upload image case)
    let { imgHeight, imgWidth } = this.state;
    if (!imgHeight || !imgWidth) {
      if (imgActualInitialHeight && imgActualInitialWidth) {
        imgHeight = imgActualInitialHeight;
        imgWidth = imgActualInitialWidth;
      } else {
        imgHeight = INITIAL_IMAGE_HEIGHT;
        imgWidth = INITIAL_IMAGE_WIDTH;
      }
    }

    const unit = "px";
    const { totalRowItems } = this.props;
    /**
     * Here we have to caluculate the correct width of image
     * 1'st have to caluculate the correct container width,
     *  - as the max container width is "MAX_IMAGE_WIDTH" & have to substract 8px(due to  padding),
     *  - if there are more than 1 row items then, container width = (MAX_IMAGE_WIDTH - 8 - 15 * (totalRowItems - 1)) / totalRowItems
     * 2'nd have to get the valid image width,
     *  - if the current image width is less than or equall to correct container width then, take the current image width
     *    else take container width
     */
    let containerWidth = (MAX_IMAGE_WIDTH - 8) / totalRowItems;
    if (this.props.totalRowItems > 1) {
      containerWidth =
        (MAX_IMAGE_WIDTH - 8 - 15 * (totalRowItems - 1)) / totalRowItems;
    }
    let getValidWidth = containerWidth;
    if (imgWidth <= containerWidth) {
      getValidWidth = imgWidth;
    }
    let getValidHeight = imgHeight;
    if (imgHeight > MAX_IMAGE_HEIGHT) {
      getValidHeight = MAX_IMAGE_HEIGHT;
    }
    const imgStyles = {
      width: `${getValidWidth}${unit}`,
      height: `${getValidHeight}${unit}`,
    };

    const {
      allowResize,
      isImageField,
      showImageFieldHighlight,
    } = this._getImageRenderingDetails();

    // Get the correct image resize handles
    let decideResizeHandles = [];
    if (isImageField) {
      if (allowResize && !this.props.isImagefieldLocked) {
        decideResizeHandles = ["sw", "se", "nw", "ne", "w", "e", "n", "s"];
      }
    } else if (!this.props.isLocked || !this.props.lock) {
      decideResizeHandles = ["sw", "se", "nw", "ne", "w", "e", "n", "s"];
    }

    // Image field heighlight class names
    let getResizeContainerClassName = "resizable-img-container ";
    if (showImageFieldHighlight) {
      getResizeContainerClassName += " image-field-highlight ";
      if (this.props.imageFormFieldDetails.value) {
        getResizeContainerClassName += " filled-highlight ";
      } else {
        getResizeContainerClassName += " empty-highlight ";
      }
    }

    // Resizing Image. Dynamic width and height of container.
    return (
      <Resizable
        height={Number(getValidHeight)}
        width={Number(getValidWidth)}
        onResize={this._onResize}
        onResizeStop={this._handleOnResizeStop}
        lockAspectRatio
        resizeHandles={decideResizeHandles}>
        <div
          onDoubleClick={this._browseFile}
          style={imgStyles}
          ref={this.imgContainerRef}
          className={getResizeContainerClassName}
          id={
            this.props.section.clauseOptions
              ? `image_field_${this.props.section.clauseOptions.id}`
              : `custom_image-id_${this.props.section.id}`
          }>
          {imageHTML}
        </div>
      </Resizable>
    );
  };

  _browseFile = () => {
    const { isImageField } = this._getImageRenderingDetails();
    if (!this.props.isLocked && !this.props.lock && !isImageField) {
      this.props.openFileBrowser();
    }
  };

  _handleOnClick = event => {
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
    const { isComponentActive, isLocked, section, data } = this.props;
    const { allowActiveOnClick } = this._getImageRenderingDetails();
    if (
      (!isLocked || (allowActiveOnClick && !this.props.isImagefieldLocked)) &&
      !isComponentActive &&
      section
    ) {
      let decidePreventFlag = data ? true : undefined;
      if (allowActiveOnClick) {
        decidePreventFlag = true;
      }
      this.props.updateDocumentClickedComponentID(
        section.id,
        decidePreventFlag,
        section.type,
      );
      this.props.createDocSectionRef(React.createRef());
    }
  };

  _handleOnBlur = event => {
    event.preventDefault();
    const { isComponentActive, preventClickedComponentIdUpdate } = this.props;
    if (isComponentActive && preventClickedComponentIdUpdate) {
      this.props.updateDocumentClickedComponentID(null, true, null);
      this.props.disablePlusBtnHandler(false);
    }
  };

  _getBlockId = () => {
    let getBlockId = this.props.section.id;
    if (this.props.componentMeta.blockType === "COVERPAGE") {
      getBlockId = this.props.componentMeta.coverpageId;
    } else if (this.props.componentMeta.blockType === "HEADER") {
      getBlockId = this.props.componentMeta.headerId;
    } else if (this.props.componentMeta.blockType === "FOOTER") {
      getBlockId = this.props.componentMeta.footerId;
    }
    return getBlockId;
  };

  _convertToImageField = () => {
    this.props.convertToImageField({
      section: this.props.section,
      blockType: this.props.componentMeta.blockType,
      blockID: this._getBlockId(),
    });
  };

  _editImageField = () => {
    const blockID = this._getBlockId();
    this.props.editImageFormField(
      this.props.section,
      this.props.imageFormFieldDetails,
      blockID,
      this.props.componentMeta.blockType,
    );
  };

  // convert image to image form field action button
  _imageFormFieldActionButton = () => {
    const { mode, section } = this.props;
    const { clauseOptions, id } = section;

    // check if image form field feature is allowed for this mode or not
    if (!COMPONENT_FEATURE[mode].createClauseOption) {
      return null;
    }

    // check if image form field feature is allowed for this component type
    if (section.type !== "Image") {
      return null;
    }

    if (this.props.totalRowItems === 1 && clauseOptions) {
      // if image form field is already attached then, show the both Edit and Remove button
      return (
        <React.Fragment>
          <Tooltip
            placement="top"
            overlay="Edit image form field"
            align={{ offset: [0, 3] }}>
            <div className="customimage-convert-imagefield-btn-wrapper edit-field-btn-wrapper">
              <span
                className="image-form-field-btn edit-field-btn"
                data-test="customimage-edit-image-form-field-btn"
                onMouseDown={this._editImageField}
                onKeyDown={() => {}}
                role="button"
                tabIndex={0}>
                <FontAwesomeIcon
                  icon={["far", "edit"]}
                  className="image-form-field-icon"
                />
                Edit placeholder
              </span>
            </div>
          </Tooltip>
          <Tooltip
            placement="top"
            overlay="Remove image form field"
            align={{ offset: [0, 3] }}>
            <div className="customimage-convert-imagefield-btn-wrapper remove-field-btn-wrapper">
              <span
                className="image-form-field-btn remove-field-btn"
                data-test="customimage-remove-image-form-field-btn"
                onMouseDown={() =>
                  this.setState({ showRemoveImageFormFieldConfirmation: true })
                }
                onKeyDown={() => {}}
                role="button"
                tabIndex={0}>
                <FontAwesomeIcon
                  icon={["far", "times"]}
                  className="image-form-field-icon"
                />
                Remove placeholder
              </span>
            </div>
          </Tooltip>
        </React.Fragment>
      );
    }

    if (section && section.lock) {
      return (
        <Tooltip
          placement="top"
          overlay="Cannot convert to image field for locked blocks"
          align={{ offset: [0, 3] }}>
          <div className="customimage-convert-imagefield-btn-wrapper">
            <span
              className="add-clause-btn disabled"
              role="button"
              tabIndex={0}
              onMouseDown={() =>
                this.props.updateDocumentClickedComponentID(
                  section.id,
                  false,
                  section.type,
                )
              }>
              <FontAwesomeIcon icon={["far", "layer-plus"]} />
              Convert to image field
            </span>
          </div>
        </Tooltip>
      );
    }
    // check if clause options is already attached or not
    return !clauseOptions ? (
      <div
        className={`customimage-convert-imagefield-btn-wrapper create-field-btn-wrapper ${
          this.props.totalRowItems > 1 ? " only-icon " : ""
        }`}>
        <ConvertToImageFieldBtn
          btnAction={this._convertToImageField}
          id={id}
          toggleUpgradePopup={() => {
            this.setState({ showConvertImageFormFieldUpgradePopup: true });
          }}
          btnTitle={
            this.props.totalRowItems === 1
              ? " Convert to image placeholder"
              : ""
          }
        />
      </div>
    ) : null;
  };

  _removeImageFormField = () => {
    // check if clause option feature is allowed for this mode or not
    const updatedSection = {
      ...this.props.section,
      clauseOptions: null,
      form_field: [],
    };
    this.props.handleBlur(updatedSection, undefined, false, this.props.section);
  };

  _getImageFieldLabel = () =>
    this.props.totalRowItems >= 2 ? (
      <Tooltip
        placement="top"
        overlay={
          this.props.imageFormFieldDetails
            ? this.props.imageFormFieldDetails.label
            : "Image placeholder"
        }
        align={{ offset: [0, 3] }}>
        <div className="clause-product-name customimage-field-label right-positioned-label ">
          <span className="label-text">
            {" "}
            <FontAwesomeIcon
              icon={["far", "info-circle"]}
              className="edit-icon"
            />
          </span>
        </div>
      </Tooltip>
    ) : (
      <div className="clause-product-name customimage-field-label">
        <span className="label-text">
          {" "}
          <FontAwesomeIcon
            icon={["far", "info-circle"]}
            className="edit-icon"
          />{" "}
          {this.props.imageFormFieldDetails
            ? this.props.imageFormFieldDetails.label
            : "Image placeholder"}
        </span>
      </div>
    );

  _getImageDetails = (isImageField, getDefaultValue, getFieldValue) => {
    let imageRawData = "";
    // Get the correct image data
    if (isImageField) {
      if (getDefaultValue) {
        imageRawData = this.props.section.clauseOptions
          ? this.props.section.clauseOptions.value || ""
          : "";
      } else if (getFieldValue) {
        imageRawData = this.props.imageFormFieldDetails
          ? this.props.imageFormFieldDetails.value || ""
          : "";
      }
    } else {
      imageRawData = this.props.data || "";
    }

    // If image data then, parse it and get the required details(ex: src, width, height, etc)
    const imageDetails = {};
    if (imageRawData) {
      if (this._isHtmlString(imageRawData)) {
        imageDetails.imageData = this._getBase64FromHtml(imageRawData);
      } else if (this._isBase64(imageRawData)) {
        imageDetails.imageData = imageRawData;
      } else {
        try {
          const {
            image,
            altText,
            imgHeight,
            imgJustify,
            imgWidth,
          } = JSON.parse(imageRawData);
          imageDetails.altTextData = altText;
          imageDetails.imageData = image;
          imageDetails.imgHeight = imgHeight;
          imageDetails.imgJustify = imgJustify;
          imageDetails.imgWidth = imgWidth;
        } catch (e) {
          imageDetails.imageData = null;
        }
      }
    }
    return imageDetails;
  };

  _getJustifyButtons = (imageData, isImageField, showJustifyButtons) => {
    let justifyButtons = null;
    if (
      (imageData || isImageField) &&
      (!this.props.draggedSection || this.props.draggedFrom === "new-component")
    ) {
      // For one item in a N-Col row
      if (this.props.totalRowItems === 1) {
        justifyButtons = !(isImageField && !showJustifyButtons) ? (
          <JustificationButtons
            handleJustification={this._handleJustification}
            position={this.state.imgJustify}
            mode={this.props.mode}
            data-test="justification-buttons-component"
          />
        ) : null;
      } else if (this.props.isComponentActive) {
        justifyButtons = (
          <CustomImageOptions
            mode={this.props.mode}
            optionRef={this.optionsDropDownRef}
            handleJustification={this._handleJustification}
            disablePlusBtnHandler={this.props.disablePlusBtnHandler}
            editImageField={this._editImageField}
            removeImageField={() =>
              this.setState({ showRemoveImageFormFieldConfirmation: true })
            }
            position={this.state.imgJustify}
            openFileBrowser={this.props.openFileBrowser}
            addAltText={this.props.addAltText}
            data-test="custom-image-options-component"
            isImageFormField={isImageField}
            showJustifyButtons={showJustifyButtons}
          />
        );
      }
    }
    return justifyButtons;
  };

  // TO_DO :: need to ugrade the method
  async _updateImageUI() {
    let { data } = this.props;
    const {
      isImageField,
      getDefaultValue,
      getFieldValue,
    } = this._getImageRenderingDetails();

    // Geting the correct image data form the image field
    if (isImageField) {
      let dataValue = "";
      if (getDefaultValue) {
        dataValue = this.props.section.clauseOptions.value;
      } else if (getFieldValue) {
        dataValue = this.props.imageFormFieldDetails.value;
      }
      if (typeof dataValue === "string" && dataValue.length > 0) {
        data = dataValue;
      } else {
        data = "";
      }
    }
    // 1. If it is a case of the image field in form mode document
    // then, need to reset the "imgWidth" and "imgHeight" so that image field will render
    // with it's actual width and height
    if (
      isImageField &&
      this.props.mode === DOCUMENTS.DOCUMENT_MODE.DOCUMENT &&
      this.props.showForm
    ) {
      let tempImgJustify = null;
      if (data) {
        if (this._isBase64(data)) {
          await this.getImageDimension(data);
        } else {
          try {
            const {
              image,
              imgJustify,
              imgHeight,
              imgWidth,
              altText,
            } = JSON.parse(data);
            tempImgJustify = imgJustify;
            if (imgHeight && imgWidth) {
              this.setState(prevState => ({
                imgWidth,
                imgHeight,
                imgJustify: tempImgJustify || prevState.imgJustify,
                altText,
              }));
            } else {
              // if there are no widht or height, then get it
              await this.getImageDimension(image, imgJustify, altText);
            }
          } catch (error) {
            this.setState({ imgWidth: null, imgHeight: null });
          }
        }
      }
    } else if (data && !this._isBase64(data)) {
      const {
        image,
        imgHeight,
        imgWidth,
        imgJustify = "flex-start",
        altText,
      } = JSON.parse(data);
      const { width, height } = this._reduceDimensions(imgWidth, imgHeight);
      if (width && height) {
        this.setState({
          imgHeight: height,
          imgJustify,
          imgWidth: width,
          altText,
        });
      } else {
        // if there are no widht or height, then get it
        await this.getImageDimension(image, imgJustify, altText);
      }
    } else if (this._isHtmlString(data)) {
      const { section, handleBlur } = this.props;
      const newData = this._getBase64FromHtml(data);
      const newSection = cloneDeep(section);
      newSection.data = newData;
      await handleBlur(newSection, undefined, undefined, { ...section });
      await this.getImageDimension(newData);
    } else {
      await this.getImageDimension(data);
    }
  }

  render() {
    const { section, isLocked, lock } = this.props;
    const { clauseOptions } = section;
    const imgRenderDetails = this._getImageRenderingDetails();

    // IMAGE DATA :: Get the Image Data in a correct fomat
    const {
      altTextData,
      imageData,
      imgWidth,
      imgHeight,
    } = this._getImageDetails(
      imgRenderDetails.isImageField,
      imgRenderDetails.getDefaultValue,
      imgRenderDetails.getFieldValue,
    );

    // IMAGE COMPONENT :: Get the Image Component
    const imageComp = imageData
      ? this._getImageComponent(imageData, altTextData, imgWidth, imgHeight)
      : null;

    // JUSTIFY BUTTONS :: Get the Justify Buttons and styles
    const justifyButtons =
      !isLocked && !lock
        ? this._getJustifyButtons(
            imageData,
            imgRenderDetails.isImageField,
            imgRenderDetails.showJustifyButtons,
          )
        : null;

    // Convert image field action buttonw wrapper class name
    let convertImgFormFieldClassName =
      "customimage-convert-imagefield-wrapper ";
    if (this.props.totalRowItems > 1) {
      convertImgFormFieldClassName += " dropdown-case ";
    }
    if (clauseOptions) {
      if (imageData) {
        convertImgFormFieldClassName += " with-image-form-field ";
      } else {
        convertImgFormFieldClassName += " without-image-form-field ";
      }
    } else if (imageData) {
      convertImgFormFieldClassName += " with-image-create-form-field ";
    }

    return (
      <React.Fragment>
        {/* ----- Remove image form field placeholder section ----- */}
        {this.state.showRemoveImageFormFieldConfirmation && (
          <ConfirmationBox
            show={this.state.showRemoveImageFormFieldConfirmation}
            name="remove-image-form-field"
            closePopup={() =>
              this.setState({ showRemoveImageFormFieldConfirmation: false })
            }
            header="Are you sure you want to Remove image field with this section ?"
            subHeader="Once removed, you will not be able to undo the process. Any workflows associated with this image field also will be removed."
            checkboxLabel="Yes, I want to remove."
            confirmAction={this._removeImageFormField}
            okBtnText="REMOVE PLACEHOLDER"
          />
        )}

        {/* -----  Convert to image field upgrade popup section ----- */}
        {this.state.showConvertImageFormFieldUpgradePopup && (
          <UpgradePopUp
            featureName={FEATURES_NAME.TEMPLATES_IMAGE_FIELD}
            onClose={() => {
              this.setState({ showConvertImageFormFieldUpgradePopup: false });
            }}
          />
        )}

        {/* ----- Image field label section ----- */}
        {imgRenderDetails.isImageField &&
          imgRenderDetails.showImageFieldLabel &&
          this._getImageFieldLabel()}

        <div
          className={`image-component ${
            this.props.isComponentActive ? "active-image" : ""
          }`}
          style={
            imageData && imageComp
              ? {
                  justifyContent: this.state.imgJustify,
                  display: "flex",
                }
              : {}
          }
          id={section.id}
          key={section.id}
          onClick={this._handleOnClick}
          onKeyPress={() => {}}
          onBlur={this._handleOnBlur}
          role="button"
          tabIndex={0}>
          {/* ----- Error Section ----- */}
          {this.state.errMsg && (
            <div className="error-message-wrapper filepond-error-position">
              <span className="error-message-icon">
                <FontAwesomeIcon icon={["far", "times-circle"]} />
              </span>
              {this.state.errMsg}
            </div>
          )}

          {/* ----- Image Section ----- */}
          {imageComp}

          {/* ----- Upload Image Placeholder Section----- */}
          {imgRenderDetails.showImagePlaceholder && (
            <div className={!isLocked && !imageData ? " " : "filepond-wrapper"}>
              <DropzoneFiles
                disabled={lock || isLocked}
                multiple={false}
                imageData={imageData}
                onFilesAdded={this._onImageUpload}
                toggleBrowse={this.props.replaceImage}
                totalRowItems={this.props.totalRowItems}
                checkImgUploadLoader={this.state.checkImgUploadLoader}
                uploadImageTypeMessage="Only .jpeg, .jpg and .png files are supported"
              />
            </div>
          )}

          {/* ----- Empty image form field placeholder ----- */}
          {imgRenderDetails.showImageFieldPlaceholder && !imageData && (
            <div
              className="customimage-formfield-placeholder"
              id={`image_field_${this.props.section.clauseOptions.id}`}>
              <span
                className={`formfield-placeholder ${
                  this.props.totalRowItems > 3 ? " small-size " : " "
                }`}>
                <FontAwesomeIcon
                  icon={["far", "image"]}
                  className="image-placeholder-icon"
                />
                {this.props.totalRowItems === 3 && (
                  <span className="image-placeholder-label">
                    Image placeholder
                  </span>
                )}
                {this.props.totalRowItems < 3 && (
                  <span className="image-placeholder-label">
                    This is an image placeholder
                  </span>
                )}
              </span>
            </div>
          )}

          {/* ----- Image justification buttons section ----- */}
          {justifyButtons}

          {/* ----- Convert to image placeholder action button section ----- */}
          {this.props.isComponentActive && !isLocked && !lock && (
            <div className={convertImgFormFieldClassName}>
              {this._imageFormFieldActionButton()}
            </div>
          )}

          {/* ----- Image size display section ----- */}
          {imageData &&
            this.props.isComponentActive &&
            this.imgContainerRef &&
            this.imgContainerRef.current &&
            this.imgContainerRef.current.clientWidth &&
            this.imgContainerRef.current.clientHeight && (
              <div className="custom-image-size remove-from-export">
                W: {parseInt(this.imgContainerRef.current.clientWidth, 10)} px
                <br /> H:{" "}
                {parseInt(this.imgContainerRef.current.clientHeight, 10)} px
              </div>
            )}
        </div>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state, props) => {
  const { mode, componentMeta } = props;
  const {
    components,
    clickedActiveComponentId,
    preventClickedComponentIdUpdate,
    document,
    forceUpdateEditorSectionId,
    draggedSection,
    docRecipientList,
  } = state.DocumentStore;
  const { recipientType } = state.SharedDocument;

  let isComponentActive = false;
  const {
    image_base_url: imageBaseUrl,
    showForm,
    scope: { write, fill } = {},
    isLocked,
    stage,
    template_type: templateType,
  } = document;
  const section =
    getComponentFromInfo(components, componentMeta) || props.component;
  if (clickedActiveComponentId === section.id) isComponentActive = true;

  const { data, lock } = section;

  const CURRENT_USER = readDataFromLocalStorage("revvsalesUser");
  // For internal user
  const currentUserIsFiller = docRecipientList.some(
    item =>
      item.user &&
      item.user.id === CURRENT_USER.id &&
      item.recipient_type === "FILLER",
  );

  let lockedInStage = true;
  if (
    stage === "DRAFT" ||
    (stage === "SENT_FOR_FILLING" &&
      (recipientType === "FILLER" || currentUserIsFiller))
  ) {
    lockedInStage = false;
  }
  const isImagefieldLocked = (isLocked && !(write || fill)) || lockedInStage;

  return {
    data,
    lock,
    mode,
    showForm,
    templateType,
    isComponentActive,
    isImagefieldLocked,
    preventClickedComponentIdUpdate,
    imageBaseUrl,
    forceUpdateEditorSectionId,
    draggedSection,
  };
};

const mapDispatchToProps = {
  convertToImageField,
  editImageFormField,
  updateDynamicFieldsInDoc,
  updateDocumentClickedComponentID,
  forceUpdateEditorSectionIdAction,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CustomImage);



// WEBPACK FOOTER //
// ./src/components/DocumentItems/CustomImage.js